
import { useCallback, useEffect, useState, useRef } from "react";
import { useSettings } from "@/context/SettingsContext";
import { useDiceValue } from "./dice/useDiceValue";
import { useRollerState, PromptStage } from "./dice/useRollerState";
import { useFeedback } from "./dice/useFeedback";
import { useLensFocus } from "./dice/useLensFocus";
import { useTwist } from "./dice/useTwist";
import { playRandomDiceSound } from "@/utils/audioUtils";

export function useRollingState(onDiceRoll?: (value: number) => void) {
  const { diceValue, isSpinning, setIsSpinning, rollDice, setDiceValue } = useDiceValue();
  const { rollerState, setRollerState, promptStage, setPromptStage, resetState } = useRollerState();
  const { feedbackStats, handleFeedback } = useFeedback();
  const { lensType, setLensType, shouldShowLensFocus, setShouldShowLensFocus, selectLensFocus } = useLensFocus();
  const { twistActive, activateTwist, resetTwist } = useTwist();
  
  const [rerollCount, setRerollCount] = useState(0);
  
  // Refs to track state between renders
  const hasCalledOnDiceRoll = useRef<boolean>(false);
  const currentProcessedValue = useRef<number>(0);
  const isComponentMounted = useRef<boolean>(true);
  
  const { autoProceed, enableLensFocus } = useSettings();

  // Reset to welcome state when mounted and cleanup on unmount
  useEffect(() => {
    console.log("useRollingState: Initializing, resetting state");
    resetState();
    resetTwist();
    setRerollCount(0);
    setLensType(undefined);
    setShouldShowLensFocus(false);
    hasCalledOnDiceRoll.current = false;
    currentProcessedValue.current = 0;
    isComponentMounted.current = true;
    
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  // Handle the prompt stages with delays or manual advancement - PREVENT LOOPS
  useEffect(() => {
    if (!isComponentMounted.current) return;
    
    console.log("=== useRollingState: Effect triggered ===", { 
      rollerState, 
      promptStage, 
      diceValue,
      hasCalledOnDiceRoll: hasCalledOnDiceRoll.current,
      currentProcessedValue: currentProcessedValue.current
    });
    
    if (rollerState === "result") {
      // First prompt appears immediately
      if (promptStage === "none") {
        console.log("useRollingState: Setting prompt stage to perception");
        setPromptStage("perception");
        
        // Only call callback with valid dice value AND only if we haven't processed this value yet
        if (onDiceRoll && diceValue > 0 && diceValue <= 6 && !hasCalledOnDiceRoll.current && diceValue !== currentProcessedValue.current) {
          console.log("=== useRollingState: CALLING onDiceRoll with dice value:", diceValue, "===");
          hasCalledOnDiceRoll.current = true;
          currentProcessedValue.current = diceValue;
          onDiceRoll(diceValue);
        } else {
          console.log("useRollingState: NOT calling onDiceRoll", {
            hasCallback: !!onDiceRoll,
            validValue: diceValue > 0 && diceValue <= 6,
            notAlreadyCalled: !hasCalledOnDiceRoll.current,
            differentValue: diceValue !== currentProcessedValue.current,
            diceValue,
            currentProcessed: currentProcessedValue.current,
            hasCalledFlag: hasCalledOnDiceRoll.current
          });
        }
      }
      
      let intentionTimer: NodeJS.Timeout;
      let nextStepTimer: NodeJS.Timeout;
      
      if (autoProceed && promptStage === "perception") {
        // Show the "intention" prompt after a delay if auto-proceed is enabled
        intentionTimer = setTimeout(() => {
          if (isComponentMounted.current) {
            console.log("useRollingState: Auto-advancing to intention");
            setPromptStage("intention");
          }
        }, 1500);
        
        // Show the "nextStep" prompt after a longer delay if auto-proceed is enabled
        nextStepTimer = setTimeout(() => {
          if (isComponentMounted.current) {
            console.log("useRollingState: Auto-advancing to nextStep");
            setPromptStage("nextStep");
          }
        }, 3000);
        
        return () => {
          clearTimeout(intentionTimer);
          clearTimeout(nextStepTimer);
        };
      }
    }
  }, [rollerState, autoProceed, diceValue, onDiceRoll, promptStage]);

  // Progress to the next stage manually
  const advancePrompt = useCallback(() => {
    console.log("useRollingState: Manually advancing prompt stage from:", promptStage);
    if (promptStage === "perception") {
      setPromptStage("intention");
    } else if (promptStage === "intention") {
      setPromptStage("nextStep");
    } else if (promptStage === "nextStep") {
      console.log("useRollingState: Already at nextStep stage, no further advancement needed");
    }
  }, [promptStage, setPromptStage]);

  // Handle feedback submission
  const handleFeedbackSubmission = useCallback((feedbackType: "positive" | "neutral" | "negative") => {
    const result = handleFeedback(feedbackType);
    
    if (result === "negative" && enableLensFocus) {
      const shouldOfferLens = Math.random() < 0.7;
      if (shouldOfferLens) {
        setShouldShowLensFocus(true);
      }
    }
  }, [handleFeedback, enableLensFocus, setShouldShowLensFocus]);

  // Re-roll the perception prompt with better error handling
  const reRollPrompt = useCallback(() => {
    console.log("useRollingState: Re-rolling prompt - resetting flags");
    setIsSpinning(true);
    setPromptStage("none");
    setRerollCount(prev => prev + 1);
    
    // Reset the flags for re-roll
    hasCalledOnDiceRoll.current = false;
    currentProcessedValue.current = 0;
    
    // Play random dice sound with error handling
    playRandomDiceSound().catch(error => {
      console.warn("Error playing dice sound during re-roll:", error);
    });
    
    // Generate a new dice value that's different from the current one
    let newValue;
    do {
      newValue = Math.floor(Math.random() * 6) + 1;
    } while (newValue === diceValue);
    
    // Simulate rolling animation for 1 second
    const spinDuration = 1000;
    const startTime = Date.now();
    
    const spinInterval = setInterval(() => {
      if (!isComponentMounted.current) {
        clearInterval(spinInterval);
        return;
      }
      
      const randomValue = Math.floor(Math.random() * 6) + 1;
      setDiceValue(randomValue);
      
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime >= spinDuration) {
        clearInterval(spinInterval);
        
        // Final value
        setDiceValue(newValue);
        setIsSpinning(false);
        
        // Show the new perception prompt
        setTimeout(() => {
          if (isComponentMounted.current) {
            setPromptStage("perception");
          }
        }, 100);
        
        // Potentially offer lens focus after multiple re-rolls
        if (rerollCount >= 2 && enableLensFocus) {
          const shouldOfferLens = Math.random() < 0.3;
          if (shouldOfferLens) {
            setShouldShowLensFocus(true);
          }
        }
      }
    }, 100);
    
    // Cleanup function to handle unmounting during animation
    return () => clearInterval(spinInterval);
  }, [diceValue, setDiceValue, setIsSpinning, setPromptStage, rerollCount, enableLensFocus, setShouldShowLensFocus]);

  const startRolling = useCallback(async () => {
    console.log("useRollingState: Starting roll... resetting flags");
    setPromptStage("none");
    setRollerState("rolling");
    resetTwist();
    setRerollCount(0);
    setLensType(undefined);
    setShouldShowLensFocus(false);
    
    // Reset the flags for new roll
    hasCalledOnDiceRoll.current = false;
    currentProcessedValue.current = 0;
    
    try {
      const finalValue = await rollDice(1500);
      
      if (isComponentMounted.current) {
        console.log("useRollingState: Roll completed with value:", finalValue);
        setRollerState("result");
      }
    } catch (error) {
      console.error("Error during dice roll:", error);
      if (isComponentMounted.current) {
        // Handle error gracefully
        setRollerState("welcome");
        setIsSpinning(false);
      }
    }
  }, [rollDice, setPromptStage, setRollerState, resetTwist, setLensType, setShouldShowLensFocus, setIsSpinning]);

  const rollAgain = useCallback(() => {
    console.log("useRollingState: Rolling again, resetting everything including flags");
    resetState();
    resetTwist();
    setRerollCount(0);
    setLensType(undefined);
    setShouldShowLensFocus(false);
    hasCalledOnDiceRoll.current = false;
    currentProcessedValue.current = 0;
  }, [resetState, resetTwist, setLensType, setShouldShowLensFocus]);

  return {
    diceValue,
    rollerState,
    promptStage,
    startRolling,
    rollAgain,
    advancePrompt,
    reRollPrompt,
    activateTwist,
    selectLensFocus,
    handleFeedback: handleFeedbackSubmission,
    twistActive,
    isRolling: rollerState === "rolling",
    showPrompts: rollerState === "result",
    isSpinning,
    rerollCount,
    lensType,
    feedbackStats,
    shouldShowLensFocus,
    setPromptStage,
    setRollerState,
  };
}
